/*
 * Product gallery (Swiper 11) â€” dedicated, self-contained init.
 *
 * Why this exists instead of the global `data-slider-selector` init:
 * the global SwiperConfigurator does `JSON.parse(config)` and passes the
 * result straight into `new Swiper()`. That means the config can only hold
 * plain data â€” it cannot carry the callback functions we need for the
 * "01 / 08 + progress bar" pagination, the per-breakpoint thumbnail
 * direction, or the live progress updates while dragging. So this script
 * owns the gallery's Swiper instances directly. It does NOT touch the
 * global init (our swipers never get the `data-slider-selector` attribute).
 *
 * All behaviour is driven by data-* attributes / CSS variables that the
 * Liquid snippet writes from the theme settings, so the markup stays clean.
 */
(function () {
  'use strict';

  var DESKTOP_MIN = 750; // matches the theme's desktop breakpoint

  function dir(position) {
    // Vertical filmstrip only when thumbnails sit to the left.
    return position === 'left' ? 'vertical' : 'horizontal';
  }

  function pad(value, length) {
    var str = String(value);
    while (str.length < length) str = '0' + str;
    return str;
  }

  function ProductGallery(root) {
    this.root = root;
    root.productGallery = this;
    this.main = root.querySelector('.main-gallery__swiper');
    this.thumbsEl = root.querySelector('.thumbs-gallery__swiper');
    this.paginationStyle = root.dataset.pagination || 'numbers';

    // Variant -> featured media map (written by the Liquid snippet). Used to
    // move the gallery to the selected variant's image. Parsed even when there
    // is only one slide (harmless), but only acted on when a slider exists.
    this.variantMedia = {};
    var mapEl = root.querySelector('script[data-variant-media]');
    if (mapEl) {
      try { this.variantMedia = JSON.parse(mapEl.textContent) || {}; } catch (e) { this.variantMedia = {}; }
    }

    var slideCount = this.main
      ? this.main.querySelectorAll('.swiper-slide').length
      : 0;

    // Nothing to slide â€” leave the single static image as-is.
    if (slideCount <= 1) {
      root.classList.add('product-gallery--single');
      return;
    }

    this.thumbDesktop = root.dataset.thumbDesktop || 'below';
    this.thumbMobile = root.dataset.thumbMobile || 'hidden';
    this.peek = parseInt(root.dataset.peek, 10) || 0;
    this.peekGap = parseInt(root.dataset.peekGap, 10) || 0;
    this.thumbGap = parseInt(root.dataset.thumbGap, 10) || 0;

    this.numbers = {
      current: root.querySelector('.main-gallery__pagination--numbers .pagination-numbers__current'),
      total: root.querySelector('.main-gallery__pagination--numbers .pagination-numbers__total'),
      fill: root.querySelector('.main-gallery__pagination--numbers .pagination-numbers__bar-fill')
    };
    this.padLength = Math.max(2, String(slideCount).length);
    if (this.numbers.total) this.numbers.total.textContent = pad(slideCount, this.padLength);

    this.init();
  }

  ProductGallery.prototype.init = function () {
    var self = this;

    // ---- Thumbnails (init first so the main slider can reference it) ----
    if (this.thumbsEl) {
      // Swiper 11 supports overriding `direction` per breakpoint, so the
      // thumbnail strip can be vertical (left layout) on one breakpoint and
      // horizontal (below layout) on another from a single instance.
      var thumbBreakpoints = {};
      thumbBreakpoints[DESKTOP_MIN] = { direction: dir(this.thumbDesktop) };

      this.thumbsSwiper = new Swiper(this.thumbsEl, {
        slidesPerView: 'auto',
        spaceBetween: this.thumbGap,
        watchSlidesProgress: true,
        slideToClickedSlide: false,
        direction: dir(this.thumbMobile),
        breakpoints: thumbBreakpoints
      });
    }

    // Touch devices get Swiper's cssMode: it hands scrolling to the browser's
    // native momentum engine, which feels far smoother on mobile than JS-driven
    // dragging. Desktop (fine pointer) keeps JS drag so click-and-drag works.
    var coarse = window.matchMedia('(pointer: coarse)').matches;

    // ---- Main slider ----
    var options = {
      slidesPerView: 'auto',
      cssMode: coarse,
      grabCursor: !coarse,
      watchOverflow: true,
      speed: 400,
      threshold: coarse ? 0 : 2,
      // Desktop: no gap (one full slide). Mobile: gap that pairs with the
      // peek so exactly `--peek` px of the next image shows on the right.
      spaceBetween: 0,
      breakpoints: {
        0: { spaceBetween: this.peekGap },
        750: { spaceBetween: 0 }
      },
      navigation: {
        nextEl: this.root.querySelector('.gallery-actions__button--next'),
        prevEl: this.root.querySelector('.gallery-actions__button--prev')
      },
      on: {
        init: function () {
          self.updateNumbers(this);
          self.updateProgress(this);
        },
        slideChange: function () {
          self.updateNumbers(this);
          self.updateProgress(this);
          self.pauseVideos();
        },
        progress: function () {
          self.updateProgress(this);
        },
        setTranslate: function () {
          self.updateProgress(this);
        }
      }
    };

    if (this.thumbsSwiper) {
      options.thumbs = { swiper: this.thumbsSwiper };
    }

    // Built-in bullet pagination only for the "dots" style. The "numbers"
    // style is rendered/updated by hand (Swiper's fraction type can't do
    // zero-padding + a progress bar through plain config).
    if (this.paginationStyle === 'dots') {
      options.pagination = {
        el: this.root.querySelector('.main-gallery__pagination--dots'),
        type: 'bullets',
        clickable: true
      };
    }

    this.mainSwiper = new Swiper(this.main, options);

    // Once images finish loading, the main image height (and therefore the
    // left-thumbnail column height) can change; remeasure both sliders.
    window.addEventListener('load', function () {
      if (self.thumbsSwiper) self.thumbsSwiper.update();
      if (self.mainSwiper) self.mainSwiper.update();
    });

    // ---- Variant -> image sync ----
    this.setupVariantSync();
  };

  /* When the shopper picks a variant, slide the gallery to that variant's
     featured image. Works with any picker (swatches, radios, dropdown) because
     it reads the resulting variant id from the product form / URL rather than
     trying to interpret the option controls itself. */
  ProductGallery.prototype.setupVariantSync = function () {
    var self = this;
    // The element the variant pickers live inside; fall back to document.
    this.productInfo = this.root.closest('product-info') || document;

    var onChange = function () {
      // Defer so the theme's own variant handler updates the hidden id / URL
      // first; the second pass covers any async re-render.
      requestAnimationFrame(function () { self.syncToVariant(); });
      setTimeout(function () { self.syncToVariant(); }, 250);
    };

    this.productInfo.addEventListener('change', onChange);
    window.addEventListener('popstate', onChange);

    // Sync once on load so the initially selected variant shows its image.
    this.syncToVariant();
  };

  ProductGallery.prototype.currentVariantId = function () {
    // The product form's hidden `id` input holds the selected variant id.
    var formId = this.root.dataset.productFormId;
    var form = formId ? document.getElementById(formId) : null;
    var input = form ? form.querySelector('[name="id"]') : null;
    if (!input) {
      var scope = (this.productInfo && this.productInfo.querySelector) ? this.productInfo : document;
      input = scope.querySelector('[name="id"]');
    }
    if (input && input.value) return String(input.value);
    // Fallback: the ?variant= URL parameter the theme keeps in sync.
    var u = new URLSearchParams(window.location.search).get('variant');
    return u ? String(u) : null;
  };

  ProductGallery.prototype.syncToVariant = function () {
    if (!this.mainSwiper) return;
    var variantId = this.currentVariantId();
    if (!variantId) return;
    var mediaId = this.variantMedia[variantId];
    if (mediaId === null || typeof mediaId === 'undefined') return; // variant has no dedicated image
    this.slideToMediaId(mediaId);
  };

  ProductGallery.prototype.slideToMediaId = function (mediaId) {
    if (!this.mainSwiper) return;
    var target = mediaId == null ? '' : String(mediaId);
    var slides = this.main.querySelectorAll('.swiper-slide');
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].getAttribute('data-media-id') === target) {
        // Jump instantly (0ms) rather than animating across every slide in
        // between â€” otherwise a far-apart variant image visibly flips through
        // all the slides between, which looks like landing on a wrong image
        // and then correcting.
        if (this.mainSwiper.activeIndex !== i) this.mainSwiper.slideTo(i, 0);
        return;
      }
    }
  };

  ProductGallery.prototype.updateNumbers = function (swiper) {
    if (!this.numbers.current) return;
    this.numbers.current.textContent = pad(swiper.activeIndex + 1, this.padLength);
  };

  ProductGallery.prototype.pauseVideos = function () {
    var vids = this.main.querySelectorAll('video');
    for (var i = 0; i < vids.length; i++) {
      try {
        vids[i].pause();
      } catch (e) {}
    }
  };

  ProductGallery.prototype.updateProgress = function (swiper) {
    if (!this.numbers.fill) return;
    var p = Math.max(0, Math.min(1, swiper.progress));
    this.numbers.fill.style.transform = 'scaleX(' + p + ')';
  };

  function initAll() {
    var roots = document.querySelectorAll('[data-product-gallery]');
    if (!roots.length) return;
    roots.forEach(function (root) {
      if (root.dataset.galleryReady === 'true') return;
      root.dataset.galleryReady = 'true';
      new ProductGallery(root);
    });
  }

  // Swiper is loaded site-wide (the global init relies on it too), but guard
  // in case this script runs before the bundle is parsed.
  function boot(attempt) {
    if (typeof window.Swiper !== 'undefined') {
      initAll();
      return;
    }
    if (attempt > 60) return; // ~1s of retries, then give up quietly
    requestAnimationFrame(function () {
      boot(attempt + 1);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(0);
    });
  } else {
    boot(0);
  }
})();